import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PlatformService } from '../platform/platform.service';
import {
  DeepLinkConfig,
  DeepLinkResult,
  DeepLinkType,
  DEFAULT_DEEP_LINK_CONFIG,
  ParsedDeepLink
} from './deep-link.types';

/**
 * Service for handling deep links in the FixPlaner application.
 *
 * Supports:
 * - Web: Standard URL navigation
 * - Android: App Links (verified deep links)
 * - iOS: Universal Links
 *
 * Deep links from email buttons are automatically routed to the correct
 * frontend component based on the URL structure.
 *
 * @example
 * // In app.component.ts
 * constructor(private deepLinkHandler: DeepLinkHandlerService) {
 *   this.deepLinkHandler.initialize();
 * }
 *
 * // The service will automatically handle incoming deep links like:
 * // https://app.fixplaner.com/absence?id=123&action=approve
 * // https://app-test.fixplaner.com/absence?id=456&action=view
 */
@Injectable({
  providedIn: 'root'
})
export class DeepLinkHandlerService {
  private config: DeepLinkConfig = DEFAULT_DEEP_LINK_CONFIG;
  private initialized = false;
  private lastHandledUrl: string | null = null;

  /** Subject for tracking pending deep links (for auth redirect flow) */
  private pendingDeepLinkSubject = new BehaviorSubject<ParsedDeepLink | null>(null);
  public pendingDeepLink$: Observable<ParsedDeepLink | null> = this.pendingDeepLinkSubject.asObservable();

  /** Known FixPlaner domains for deep link validation */
  private readonly validDomains = [
    'app.fixplaner.com',
    'app-test.fixplaner.com',
    'localhost'
  ];

  constructor(
    private router: Router,
    private platformService: PlatformService,
    private ngZone: NgZone
  ) {}

  /**
   * Initialize deep link handling.
   * Should be called once during app startup (typically in app.component.ts).
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.debug('[DeepLinkHandler] Already initialized');
      return;
    }

    if (this.platformService.isMobile() && Capacitor.isPluginAvailable('App')) {
      await this.initializeCapacitorListeners();
    }

    // Check for pending deep link from previous session (after login)
    this.checkPendingDeepLink();

    this.initialized = true;
  }

  /**
   * Initialize Capacitor App plugin listeners for deep links.
   */
  private async initializeCapacitorListeners(): Promise<void> {
    // Listen for deep links while app is running
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      // Use NgZone to ensure Angular change detection runs
      this.ngZone.run(() => {
        this.handleDeepLink(event.url);
      });
    });

    // Check for launch URL (app opened via deep link when closed)
    try {
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        // Delay slightly to ensure app is fully initialized
        setTimeout(() => {
          this.ngZone.run(() => {
            this.handleDeepLink(launchUrl.url);
          });
        }, 500);
      }
    } catch (error) {
      console.warn('[DeepLinkHandler] Could not get launch URL:', error);
    }
  }

  /**
   * Handle an incoming deep link URL.
   * Parses the URL, checks authentication, and navigates to the appropriate route.
   *
   * @param url The deep link URL to handle
   * @returns Result of the deep link handling
   */
  async handleDeepLink(url: string): Promise<DeepLinkResult> {
    // Prevent duplicate handling of the same URL
    if (url === this.lastHandledUrl) {
      console.debug('[DeepLinkHandler] Duplicate URL, skipping');
      return { success: true, navigatedTo: url };
    }
    this.lastHandledUrl = url;

    // Reset after 2 seconds to allow re-handling
    setTimeout(() => {
      if (this.lastHandledUrl === url) {
        this.lastHandledUrl = null;
      }
    }, 2000);

    try {
      const parsed = this.parseDeepLink(url);

      if (parsed.type === DeepLinkType.Unknown) {
        console.warn('[DeepLinkHandler] Unknown deep link type:', url);
        return { success: false, error: 'Unknown deep link type' };
      }

      // Check if route requires authentication
      const requiresAuth = this.isProtectedRoute(parsed.path);
      const isAuthenticated = this.checkAuthentication();

      if (requiresAuth && !isAuthenticated) {
        this.storePendingDeepLink(parsed);
        await this.router.navigate(['/login']);
        return { success: true, authRedirectRequired: true };
      }

      // Navigate to the route
      const queryParams = parsed.params;
      await this.router.navigate([parsed.path], { queryParams });

      return { success: true, navigatedTo: parsed.path };
    } catch (error) {
      console.error('[DeepLinkHandler] Error handling deep link:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Parse a deep link URL into its components.
   */
  parseDeepLink(url: string): ParsedDeepLink {
    try {
      const parsedUrl = new URL(url);

      // Validate domain
      if (!this.isValidDomain(parsedUrl.hostname)) {
        console.warn('[DeepLinkHandler] Invalid domain:', parsedUrl.hostname);
        return this.createUnknownDeepLink(url);
      }

      const path = parsedUrl.pathname || '/';
      const params = this.parseQueryParams(parsedUrl.search);
      const type = this.determineDeepLinkType(path, params);

      return {
        type,
        path,
        params,
        originalUrl: url
      };
    } catch (error) {
      console.error('[DeepLinkHandler] Error parsing URL:', error);
      return this.createUnknownDeepLink(url);
    }
  }

  /**
   * Determine the deep link type based on path and parameters.
   */
  private determineDeepLinkType(path: string, params: Record<string, string>): DeepLinkType {
    const normalizedPath = path.toLowerCase();

    // Absence routes
    if (normalizedPath.includes('/absence')) {
      if (params['action'] === 'approve') return DeepLinkType.AbsenceApprove;
      if (params['id']) return DeepLinkType.AbsenceView;
      return DeepLinkType.AbsenceList;
    }

    // Employee routes
    if (normalizedPath.includes('/employee')) {
      if (params['action'] === 'edit') return DeepLinkType.EmployeeEdit;
      if (params['requestId']) return DeepLinkType.EmployeeChangeRequest;
      if (params['id']) return DeepLinkType.EmployeeProfile;
      return DeepLinkType.EmployeeProfile;
    }

    // Planner routes
    if (normalizedPath.includes('/planner')) {
      if (params['swapId']) return DeepLinkType.ShiftSwapRequest;
      if (params['shiftId']) return DeepLinkType.ShiftDetail;
      return DeepLinkType.ShiftPlan;
    }

    // Time recording routes
    if (normalizedPath.includes('/time-recording')) {
      if (params['action'] === 'edit') return DeepLinkType.TimeRecordingEdit;
      if (params['requestId']) return DeepLinkType.TimeChangeRequest;
      if (params['filter'] === 'open') return DeepLinkType.OpenClockInsAlert;
      return DeepLinkType.TimeRecordingList;
    }

    // Dashboard routes
    if (normalizedPath.includes('/dashboard')) {
      if (params['alert'] === 'compliance') return DeepLinkType.ComplianceAlert;
      if (params['alert'] === 'overtime') return DeepLinkType.OvertimeAlert;
      return DeepLinkType.DashboardOverview;
    }

    // Settings
    if (normalizedPath.includes('/settings')) {
      if (params['section'] === 'datev') return DeepLinkType.DatevExport;
      return DeepLinkType.Settings;
    }

    // Authentication routes
    if (normalizedPath.includes('/accept-invitation')) return DeepLinkType.AcceptInvitation;
    if (normalizedPath.includes('/resetpassword')) return DeepLinkType.PasswordReset;
    if (normalizedPath.includes('/email-confirmation')) return DeepLinkType.EmailConfirmation;
    if (normalizedPath.includes('/login')) return DeepLinkType.Login;

    return DeepLinkType.Unknown;
  }

  /**
   * Check if a route requires authentication.
   */
  private isProtectedRoute(path: string): boolean {
    const normalizedPath = path.toLowerCase();

    // Check if it's explicitly public
    for (const publicRoute of this.config.publicRoutes) {
      if (normalizedPath.startsWith(publicRoute.toLowerCase())) {
        return false;
      }
    }

    // Check if it's a protected route
    for (const protectedRoute of this.config.protectedRoutes) {
      if (normalizedPath.startsWith(protectedRoute.toLowerCase())) {
        return true;
      }
    }

    // Default to requiring auth for unknown routes
    return true;
  }

  /**
   * Check if user is currently authenticated.
   */
  private checkAuthentication(): boolean {
    // Check for auth token in localStorage
    const token = localStorage.getItem('token');
    return !!token;
  }

  /**
   * Store a pending deep link for after authentication.
   */
  private storePendingDeepLink(link: ParsedDeepLink): void {
    sessionStorage.setItem(this.config.pendingLinkStorageKey, JSON.stringify(link));
    this.pendingDeepLinkSubject.next(link);
  }

  /**
   * Check for and handle pending deep link after login.
   * Should be called after successful authentication.
   */
  checkPendingDeepLink(): ParsedDeepLink | null {
    const stored = sessionStorage.getItem(this.config.pendingLinkStorageKey);
    if (stored) {
      try {
        const pending = JSON.parse(stored) as ParsedDeepLink;
        this.pendingDeepLinkSubject.next(pending);
        return pending;
      } catch {
        sessionStorage.removeItem(this.config.pendingLinkStorageKey);
      }
    }
    return null;
  }

  /**
   * Process pending deep link after successful login.
   * Navigates to the stored deep link and clears it.
   */
  async processPendingDeepLink(): Promise<DeepLinkResult | null> {
    const pending = this.checkPendingDeepLink();
    if (!pending) {
      return null;
    }

    sessionStorage.removeItem(this.config.pendingLinkStorageKey);
    this.pendingDeepLinkSubject.next(null);

    await this.router.navigate([pending.path], { queryParams: pending.params });

    return { success: true, navigatedTo: pending.path };
  }

  /**
   * Clear any pending deep link.
   */
  clearPendingDeepLink(): void {
    sessionStorage.removeItem(this.config.pendingLinkStorageKey);
    this.pendingDeepLinkSubject.next(null);
  }

  /**
   * Check if a domain is valid for FixPlaner deep links.
   */
  private isValidDomain(hostname: string): boolean {
    return this.validDomains.some(domain =>
      hostname === domain || hostname.endsWith('.' + domain)
    );
  }

  /**
   * Parse query parameters from a URL search string.
   */
  private parseQueryParams(search: string): Record<string, string> {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Create an unknown deep link result.
   */
  private createUnknownDeepLink(url: string): ParsedDeepLink {
    return {
      type: DeepLinkType.Unknown,
      path: '/',
      params: {},
      originalUrl: url
    };
  }

  /**
   * Update configuration for deep link handling.
   */
  setConfig(config: Partial<DeepLinkConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
